import CircularProgress from "@mui/material/CircularProgress";
import * as types from "_graphql-types-frontend/graphql";
import { useCallback, useEffect, useMemo } from "react";
import ContentItems from "./ContentItems.component";
import { useTemplate, TemplateParamsContext } from "./Context/hooks";
import TemplateForm from "./TemplateForm";
import TemplateNav from "./TemplateNav";
import { useLocation } from "react-router-dom";
import { ErrorBoundary } from "@sentry/react";
import { useShallow } from "zustand/react/shallow";
import { TemplatePeriod } from "./types";
import RangerStalenessBanner from "./RangerStalenessBanner";
// Create a context to hold the store params

interface TemplateComponentProps {
  name: types.FieldSetType;
  ownerId: number | string;
  ownerType: types.FieldOwnerType;
  editMode: boolean;
  children?: React.ReactNode;
  simplified?: boolean;
  onIsCompleted?: () => void;
  onPublish?: () => void;
  getModifyDate?: () => Date;
  /** Active reporting period for the ongoing-reporting tab; omit for standard tabs. */
  period?: TemplatePeriod;
}

function TemplateComponent({
  name,
  ownerId,
  ownerType,
  children = null,
  getModifyDate,
  editMode,
  simplified,
  onIsCompleted,
  onPublish,
  period,
}: TemplateComponentProps): JSX.Element | null {
  // Initialize the params
  const params = {
    name,
    ownerId,
    ownerType,
    editMode,
    getModifyDate,
    simplified: simplified || false,
    onIsCompleted,
    onPublish,
    period,
  };

  const store = useTemplate(params);

  const { error, loading, content, storeEditMode, fieldMeta } = store(
    useShallow(state => ({
      error: state.error,
      loading: state.loading,
      content: state.content,
      storeEditMode: state.editMode,
      fieldMeta: state.templateData?.fieldMeta,
    }))
  );

  // #5223: Sirius AI answers that new material has made stale, plus the last
  // targeted re-run's diff. Staleness is tracked per investment (a run writes
  // the investment's AND its firm's fields), so only the investment templates
  // carry the banner.
  const getFieldDisplayName = useCallback(
    (fieldOwnerType: string, fieldKey: string): string | undefined =>
      fieldMeta?.[fieldOwnerType]?.[fieldKey]?.displayName ?? undefined,
    [fieldMeta]
  );
  const refreshRangerFieldData = useCallback(
    () => store.getState().fetchRangerFieldData(),
    [store]
  );
  const stalenessBanner =
    ownerType === types.FieldOwnerType.Investment && !simplified ? (
      <RangerStalenessBanner
        investmentId={Number(ownerId)}
        // Re-running writes fields: offer it only where the template offers
        // writes. The banner and its Review dialog stay read-only for everyone.
        canRerun={storeEditMode}
        getDisplayName={getFieldDisplayName}
        onRerunComplete={refreshRangerFieldData}
      />
    ) : null;

  let location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    const el =
      hash && document.getElementById(hash.substr(1).replace("_RETURN", ""));

    if (el) {
      console.log("ELEMENT TO SCROLL TO:", el);

      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.location.href = window.location.href.replace("_RETURN", "");
    }
  }, [location.hash]);

  // Context params to pass down
  const contextParams = useMemo(
    () => ({
      name,
      ownerId,
      ownerType,
      editMode,
      period,
    }),
    [name, ownerId, ownerType, editMode, period?.asOfDate, period?.periodType]
  );

  if (loading) {
    return (
      <div
        data-cy="loading-spinner"
        data-testid="loading-spinner"
        className="text-center"
      >
        <CircularProgress />
      </div>
    );
  }

  if (storeEditMode) {
    return (
      <TemplateParamsContext.Provider value={contextParams}>
        {error && (
          <div
            data-cy="template-error"
            data-testid="template-error"
            className="text-center"
          >
            {error.message}
          </div>
        )}

        <ErrorBoundary
          fallback={
            <div data-testid="template-error-boundary">
              <h2>Something went wrong</h2>
              <p>
                Please try again later, or contact support if the issue persists
              </p>
              {error && <p>Error: {error?.message}</p>}
            </div>
          }
          onError={(error: any) => {
            console.error(
              "Error in TemplateComponent ErrorBoundary",
              error?.message,
              error?.stack
            );
          }}
        >
          {stalenessBanner}
          <div style={{ display: "flex", marginBottom: "100px" }}>
            <TemplateNav />
            <div style={{ width: "100%" }}>
              <TemplateForm>
                {children}
                <ContentItems content={content} />
              </TemplateForm>
            </div>
          </div>
        </ErrorBoundary>
      </TemplateParamsContext.Provider>
    );
  }

  return (
    <TemplateParamsContext.Provider value={contextParams}>
      {error && (
        <div
          data-cy="template-error"
          data-testid="template-error"
          className="text-center"
        >
          {error.message}
        </div>
      )}
      <ErrorBoundary
        fallback={
          <div data-testid="template-error-boundary">
            <h2>Something went wrong</h2>
            <p>
              Please try again later, or contact support if the issue persists
            </p>
            {error && <p>Error: {error?.message}</p>}
          </div>
        }
        onError={(error: any) => {
          console.error(
            "Error in TemplateComponent ErrorBoundary",
            error?.message,
            error?.stack
          );
        }}
      >
        {children}
        {stalenessBanner}
        <div style={{ display: "flex" }}>
          <TemplateNav />
          <div style={{ width: "calc(100% - 55px)" }}>
            <ContentItems content={content} />
          </div>
        </div>
      </ErrorBoundary>
    </TemplateParamsContext.Provider>
  );
}

export default TemplateComponent;
